import TemplateHelper from './template-helper';
import SVGIcons from './svgicons';

const svgIcons = new SVGIcons();
const templateHelper = new TemplateHelper();

/**
 * Create and manage Info bars for displaying messages to user
 *
 * Usage example:
 *
 * new InfoBar({
 *   text: 'Operation successful!',
 *   type: 'warning',
 *   group: 'expense',
 *   container: document.querySelector('.expense-messages')
 * });
 *
 * @param {Object} options - InfoBar options
 * @param {String} options.text - The message shown in the bar
 * @param {String} options.type - The bar type [check/warning/error]
 * @param {String} options.group - The bar group (_hideGroup uses this to hide bars belonging to a given group)
 * @param {HTMLElement} options.container - The element in which the info bar should be prepended into
 */
class InfoBar {
  constructor(options = {}) {
    const defaults = {
      text: '',
      type: 'error',
      group: 'general',
      container: document.body,
      shouldRefresh: false,
    };

    // Apply options
    this.view = Object.assign({}, defaults, options);

    // Generate bar HTML & insert into DOM
    this.barEl = this._renderBar();

    // Ensure bar becomes visible in user's viewport
    if('scrollIntoView' in this.barEl) {
      this.barEl.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    } else {
      // TODO: Implement scrollIntoView for IE & Safari
    }

    // Bind & attach a handler for clearing info bars
    this._boundHandleClearInfos = this._handleClearInfos.bind(this);
    window.addEventListener('item:clearInfos', this._boundHandleClearInfos);

    // Bind & attach a handler for refresh requests.
    this._boundHandleRefresh = this._handleRefresh.bind(this);
    const refreshButton = this.barEl.querySelector('.info-box--refresh-page-button');
    if(refreshButton) {
      refreshButton.addEventListener('click', this._boundHandleRefresh);
    }
  }

  /**
   * Checks if event contains an InfoBar group and triggers hiding method
   * @param {CustomEvent} event - The event that triggered the handler
   */
  _handleClearInfos(event) {
    if(typeof event.detail.group === 'string') {
      this._hideGroup(event.detail.group);
    }
  }
   
  /**
   * Handles a refresh request
   * @param {CustomEvent} event - The event that triggered the handler
   */
  _handleRefresh(event) {
    // refresh with current URL
    window.location.reload();
  }

  /**
   * Hides this InfoBar instance, if it's group matches the given group
   * @param {String} group - The group to hide
   */
  _hideGroup(group) {
    if(this.view.group === group) {
      this._removeBar();
    }
  }

  /**
   * Removes this InfoBar from DOM
   */
  _removeBar() {
    this.view.container.removeChild(this.barEl);
    window.removeEventListener('item:clearInfos', this._boundHandleClearInfos);
  }

  /**
   * Generates an icon's HTML into the instance
   * @returns {String} - Icon HTML
   */
  _renderIcon() {
    if(!this.view.icon) {
      this.view.icon = this.view.type;
    }
    return svgIcons(this.view.icon);
  }

  /**
   * Renders the InfoBar template and inserts it into the DOM, according to the instance's container setting
   *
   * @returns {HTMLElement} - A reference to the newly inserted element
   */
  _renderBar() {
    this.view.iconHTML = this._renderIcon();
    return templateHelper.render(
      'info-bar',
      this.view,
      this.view.container,
      'prepend'
    );
  }
}

export default InfoBar;
